import React, { useState } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { CreateBudgetDialog } from './CreateBudgetDialog';
import { useCreateBudget } from '../hooks/useBudgetQueries';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import { useI18n } from '../hooks/useI18n.ts';
import { Budget, ContributionCalculationType } from '../types/budget';
import { DEFAULT_CURRENCY } from '../constants/currency';

interface AddBudgetButtonProps {
  showText?: boolean;
  className?: string;
  onBudgetCreated?: (budgetId: number, budget: Budget) => void;
}

export const AddBudgetButton: React.FC<AddBudgetButtonProps> = ({
  showText = false,
  className = '',
  onBudgetCreated,
}) => {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const navigate = useNavigate();
  const createBudgetMutation = useCreateBudget();
  const { t, defaults } = useI18n();
  const { showToast } = useToast();

  const handleCreateBudget = (name: string) => {
    const budget = {
      name,
      date: new Date(),
      transactions: [],
      categories: defaults.categories,
      responsibles: defaults.responsibles,
      destinations: defaults.destinations,
      settings: {
        contributionCalculationType: ContributionCalculationType.INCOME_RATIO,
        currency: DEFAULT_CURRENCY,
      },
    };

    createBudgetMutation.mutate(budget, {
      onSuccess: (budgetId) => {
        setCreateDialogOpen(false);
        showToast(t('budget.createSuccess'), 'success');

        if (onBudgetCreated) {
          onBudgetCreated(budgetId, budget);
        } else {
          // If no callback provided, just navigate to dashboard
          navigate('/budgets/current', { replace: true });
        }
      },
      onError: (error) => {
        console.error('Error creating budget:', error);
        showToast(t('budget.createError'), 'error');
      },
    });
  };

  const baseClasses =
    'flex items-center rounded-md border border-transparent bg-indigo-600 px-2.5 py-1.5 text-sm text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:bg-indigo-500 dark:text-white dark:hover:bg-indigo-400';
  const buttonClasses = className ? `${baseClasses} ${className}` : baseClasses;

  return (
    <>
      <button
        onClick={() => setCreateDialogOpen(true)}
        data-attr="add-budget-button"
        className={buttonClasses}
        title={t('budget.new')}
      >
        <PlusIcon className="h-5 w-5" />
        {showText && <span className="ml-2">{t('budget.new')}</span>}
      </button>

      <CreateBudgetDialog
        isOpen={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onConfirm={handleCreateBudget}
      />
    </>
  );
};
